import { Component, OnInit, OnDestroy, ViewChildren, QueryList, ElementRef, inject, ViewEncapsulation, AfterViewInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { Router, ActivatedRoute, RouterModule } from '@angular/router';
import { trigger, state, style, transition, animate } from '@angular/animations';
import { BreakpointObserver } from '@angular/cdk/layout';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { ToastModule } from 'primeng/toast';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject, takeUntil } from 'rxjs';
import { AuthService } from '../../core/services/auth.service';
import { ApiError } from '../../core/models/auth.models';

/**
 * Composant de vérification OTP avec animations et intégration API réelle
 * Architecture: Angular Standalone + PrimeNG + Reactive Forms
 * API: POST /api/v1/auth/verify-otp
 * DTO: VerifyOtpDto { value: number }
 * 
 * Features:
 * - Auto-focus et auto-switch entre inputs
 * - Auto-submit quand tous les champs sont remplis
 * - Support paste (Ctrl+V / Cmd+V)
 * - Countdown timer avec progress bar
 * - Animations de feedback visuel
 * - Accessibilité complète (ARIA, keyboard navigation)
 */
@Component({
  selector: 'app-verification',
  standalone: true,
  encapsulation: ViewEncapsulation.None,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterModule,
    ButtonModule,
    ProgressSpinnerModule,
    ToastModule,
    TranslateModule,
  ],
  providers: [],
  templateUrl: './verification.component.html',
  animations: [
    // Animation de fondu global
    trigger('fadeIn', [
      transition(':enter', [
        style({ opacity: 0 }),
        animate('600ms cubic-bezier(0.4, 0, 0.2, 1)', style({ opacity: 1 })),
      ]),
    ]),
    // Animation de slide up
    trigger('slideInUp', [
      transition(':enter', [
        style({ transform: 'translateY(20px)', opacity: 0 }),
        animate('400ms 200ms cubic-bezier(0.4, 0, 0.2, 1)',
          style({ transform: 'translateY(0)', opacity: 1 })),
      ]),
    ]),
    // Animation de slide pour form
    trigger('formSlide', [
      transition(':enter', [
        style({ transform: 'translateX(-20px)', opacity: 0 }),
        animate('300ms cubic-bezier(0.4, 0, 0.2, 1)',
          style({ transform: 'translateX(0)', opacity: 1 })),
      ]),
    ]),
    // Animation pour erreurs
    trigger('errorSlide', [
      transition(':enter', [
        style({ height: 0, opacity: 0, transform: 'translateY(-10px)' }),
        animate('200ms cubic-bezier(0.4, 0, 0.2, 1)',
          style({ height: '*', opacity: 1, transform: 'translateY(0)' })),
      ]),
      transition(':leave', [
        animate('150ms cubic-bezier(0.4, 0, 0.2, 1)',
          style({ height: 0, opacity: 0, transform: 'translateY(-10px)' })),
      ]),
    ]),
    // Animation pour timer
    trigger('slideDown', [
      transition(':enter', [
        style({ transform: 'translateY(-10px)', opacity: 0 }),
        animate('300ms cubic-bezier(0.4, 0, 0.2, 1)',
          style({ transform: 'translateY(0)', opacity: 1 })),
      ]),
    ]),
    // Animation de succès
    trigger('successFade', [
      transition(':enter', [
        style({ opacity: 0, transform: 'scale(0.9)' }),
        animate('400ms cubic-bezier(0.4, 0, 0.2, 1)',
          style({ opacity: 1, transform: 'scale(1)' })),
      ]),
    ]),
  ],
})
export class VerificationComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChildren('codeInput') codeInputs!: QueryList<ElementRef<HTMLInputElement>>;

  private readonly fb = inject(FormBuilder);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly authService = inject(AuthService);
  private readonly messageService = inject(MessageService);
  private readonly translate = inject(TranslateService);
  private readonly breakpointObserver = inject(BreakpointObserver);
  private readonly destroy$ = new Subject<void>();

  verificationForm!: FormGroup;
  isLoading = false;
  isResending = false;
  isSuccess = false;
  canResend = false;
  hasError = false;
  errorMessage = '';
  countdown = 60; // 60 secondes = 1 minute
  initialCountdown = 60; // Pour calculer le pourcentage
  phoneNumber = '';
  emailOrTel = '';
  private countdownInterval: ReturnType<typeof setInterval> | null = null;

  ngOnInit(): void {
    // Récupérer phone/email depuis les query params
    this.route.queryParams.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      this.phoneNumber = params['phone'] || '';
      this.emailOrTel = params['phone'] || params['email'] || '';
    });

    // Initialiser le formulaire (7 champs, aligné avec l'OTP backend)
    this.verificationForm = this.fb.group({
      code1: ['', [Validators.required, Validators.pattern(/^\d$/)]],
      code2: ['', [Validators.required, Validators.pattern(/^\d$/)]],
      code3: ['', [Validators.required, Validators.pattern(/^\d$/)]],
      code4: ['', [Validators.required, Validators.pattern(/^\d$/)]],
      code5: ['', [Validators.required, Validators.pattern(/^\d$/)]],
      code6: ['', [Validators.required, Validators.pattern(/^\d$/)]],
      code7: ['', [Validators.required, Validators.pattern(/^\d$/)]],
    });

    this.startCountdown();
  }

  ngAfterViewInit(): void {
    // Auto-focus sur le premier input après que la vue soit initialisée
    setTimeout(() => {
      const inputs = this.codeInputs.toArray();
      if (inputs[0]) {
        inputs[0].nativeElement.focus();
      }
    }, 300);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
    }
  }

  /**
   * Formater le contact (email/téléphone) pour l'affichage
   * Masque partiellement pour la sécurité
   */
  formatContact(contact: string): string {
    if (!contact) return '';

    // Si c'est un email
    if (contact.includes('@')) {
      const [username, domain] = contact.split('@');
      const maskedUsername = username.substring(0, 2) + '***' + username.substring(username.length - 1);
      return `${maskedUsername}@${domain}`;
    }

    // Si c'est un téléphone
    const cleaned = contact.replace(/\D/g, '');
    if (cleaned.length >= 9) {
      return cleaned.substring(0, 3) + ' *** ' + cleaned.substring(cleaned.length - 2);
    }

    return contact;
  }

  /**
   * Gérer le focus sur un input
   */
  onInputFocus(index: number): void {
    const inputs = this.codeInputs.toArray();
    if (inputs[index]) {
      inputs[index].nativeElement.select();
    }
  }

  startCountdown(): void {
    this.canResend = false;
    this.countdown = 60; // 60 secondes = 1 minute

    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
      this.countdownInterval = null;
    }

    this.countdownInterval = setInterval(() => {
      this.countdown--;
      if (this.countdown <= 0) {
        this.canResend = true;
        this.countdown = 0;
        if (this.countdownInterval) {
          clearInterval(this.countdownInterval);
          this.countdownInterval = null;
        }
      }
    }, 1000);
  }

  get countdownFormatted(): string {
    const minutes = Math.floor(this.countdown / 60);
    const seconds = this.countdown % 60;
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }

  /**
   * Gérer l'entrée d'un chiffre dans un input
   * Avec auto-focus, validation et feedback visuel
   */
  onCodeInput(event: Event, index: number): void {
    const input = event.target as HTMLInputElement;
    let value = input.value;

    // Réinitialiser l'erreur
    this.hasError = false;
    this.errorMessage = '';

    // Seulement des chiffres
    if (!/^\d*$/.test(value)) {
      input.value = '';
      this.verificationForm.get(`code${index + 1}`)?.setValue('');
      this.shakeInputs();
      return;
    }

    // Limiter à un seul chiffre
    if (value.length > 1) {
      value = value.slice(-1);
      input.value = value;
    }

    // Mettre à jour le contrôle du formulaire
    this.verificationForm.get(`code${index + 1}`)?.setValue(value || '');

    // Animation de remplissage
    if (value) {
      input.classList.add('filled-animation');
      setTimeout(() => input.classList.remove('filled-animation'), 200);
    }

    // Passer au champ suivant si une valeur est entrée
    if (value && index < 6) {
      const inputs = this.codeInputs.toArray();
      if (inputs[index + 1]) {
        setTimeout(() => {
          inputs[index + 1].nativeElement.focus();
        }, 50);
      }
    }

    // Auto-submit quand tous les champs sont remplis
    if (this.isFormValid && value) {
      // Donner un petit délai pour que l'utilisateur voie le dernier chiffre
      setTimeout(() => {
        if (this.isFormValid && !this.isLoading) {
          this.onSubmit();
        }
      }, 400);
    }
  }

  /**
   * Animation de secousse pour feedback d'erreur
   */
  private shakeInputs(): void {
    const container = document.querySelector('.verification-form__inputs');
    if (container) {
      container.classList.add('shake-animation');
      setTimeout(() => container.classList.remove('shake-animation'), 500);
    }
  }

  onCodeKeyDown(event: KeyboardEvent, index: number): void {
    const input = event.target as HTMLInputElement;

    // Gérer backspace
    if (event.key === 'Backspace' && !input.value && index > 0) {
      const inputs = this.codeInputs.toArray();
      if (inputs[index - 1]) {
        inputs[index - 1].nativeElement.focus();
        inputs[index - 1].nativeElement.select();
      }
    }

    // Gérer les flèches
    if (event.key === 'ArrowLeft' && index > 0) {
      event.preventDefault();
      const inputs = this.codeInputs.toArray();
      if (inputs[index - 1]) {
        inputs[index - 1].nativeElement.focus();
      }
    }

    if (event.key === 'ArrowRight' && index < 6) {
      event.preventDefault();
      const inputs = this.codeInputs.toArray();
      if (inputs[index + 1]) {
        inputs[index + 1].nativeElement.focus();
      }
    }

    // Gérer paste (Ctrl+V ou Cmd+V)
    if ((event.ctrlKey || event.metaKey) && event.key === 'v') {
      event.preventDefault();
      navigator.clipboard.readText().then((text) => {
        this.handlePaste(text);
      });
    }
  }

  onCodePaste(event: ClipboardEvent): void {
    event.preventDefault();
    const pastedData = event.clipboardData?.getData('text');
    if (pastedData) {
      this.handlePaste(pastedData);
    }
  }

  private handlePaste(text: string): void {
    const digits = text.replace(/\D/g, '').slice(0, 7); // 7 chiffres comme l'API
    const inputs = this.codeInputs.toArray();

    digits.split('').forEach((digit, index) => {
      if (index < 7 && inputs[index]) {
        const control = this.verificationForm.get(`code${index + 1}`);
        control?.setValue(digit);
        inputs[index].nativeElement.value = digit;
      }
    });

    // Focus sur le prochain champ vide ou le dernier
    const nextEmptyIndex = digits.length < 7 ? digits.length : 6;
    if (inputs[nextEmptyIndex]) {
      inputs[nextEmptyIndex].nativeElement.focus();
    }

    // Auto-submit si tous les champs sont remplis
    if (this.isFormValid) {
      setTimeout(() => {
        if (this.isFormValid) {
          this.onSubmit();
        }
      }, 300);
    }
  }

  /**
   * Soumettre le code OTP
   * API: POST /api/v1/auth/verify-otp
   * Avec animations et feedback visuel amélioré
   */
  async onSubmit(): Promise<void> {
    if (this.verificationForm.invalid || this.isLoading) {
      this.markFormGroupTouched(this.verificationForm);
      this.shakeInputs();
      return;
    }

    this.isLoading = true;
    this.hasError = false;
    this.errorMessage = '';

    const code = Object.values(this.verificationForm.value)
      .join('')
      .trim();

    if (code.length !== 7) {
      this.hasError = true;
      this.errorMessage = this.translate.instant('VERIFICATION.INVALID_CODE');
      this.isLoading = false;
      this.shakeInputs();
      return;
    }

    // Convertir en nombre (l'API attend un number)
    const otpCode = parseInt(code, 10);

    // Annoncer pour l'accessibilité
    this.announce(this.translate.instant('VERIFICATION.VERIFYING'));

    try {
      const response = await this.authService.verifyOtp({ value: otpCode });

      const isMobile = this.breakpointObserver.isMatched('(max-width: 768px)');

      if (!isMobile) {
        // Desktop: afficher l'overlay de succès et la modale
        this.isSuccess = true;
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('VERIFICATION.SUCCESS_TITLE'),
          detail: this.translate.instant('VERIFICATION.SUCCESS_MESSAGE'),
          life: 2000,
          icon: 'pi pi-check-circle',
        });
        this.announce(this.translate.instant('VERIFICATION.SUCCESS_MESSAGE'));
        await new Promise(resolve => setTimeout(resolve, 1500));
      }

      this.isLoading = false;

      // Navigation conditionnelle selon category et roles
      const targetRoute = this.authService.getDefaultRoute(isMobile);
      await this.router.navigate([targetRoute]);
    } catch (error: any) {
      this.isLoading = false;
      console.error('❌ OTP verification error:', error);
      this.handleApiError(error);
      this.shakeInputs();
    }
  }

  /**
   * Annoncer un message pour l'accessibilité
   */
  private announce(message: string): void {
    const announcement = document.createElement('div');
    announcement.setAttribute('role', 'status');
    announcement.setAttribute('aria-live', 'polite');
    announcement.className = 'sr-only';
    announcement.textContent = message;
    document.body.appendChild(announcement);
    setTimeout(() => document.body.removeChild(announcement), 1000);
  }

  /**
   * Renvoyer le code OTP
   * API: POST /api/v1/auth/login-otp
   * Avec feedback visuel et animation
   */
  async onResendCode(): Promise<void> {
    if (!this.canResend || this.isLoading || this.isResending) {
      return;
    }

    if (!this.emailOrTel) {
      this.router.navigate(['/auth/login']);
      return;
    }

    this.isResending = true;
    this.hasError = false;
    this.errorMessage = '';

    // Annoncer pour l'accessibilité
    this.announce(this.translate.instant('VERIFICATION.RESENDING'));

    try {
      const response = await this.authService.requestOtp({ emailOrTel: this.emailOrTel });

      this.isResending = false;

      // Extraire les données du wrapper API
      const otpData = response.data || response;
      const message = otpData.message || this.translate.instant('VERIFICATION.RESEND_SUCCESS_MESSAGE');

      // Success feedback
      this.messageService.add({
        severity: 'success',
        summary: this.translate.instant('VERIFICATION.RESEND_SUCCESS_TITLE'),
        detail: message,
        life: 3000,
        icon: 'pi pi-check-circle',
      });

      // Annoncer pour l'accessibilité
      this.announce(this.translate.instant('VERIFICATION.RESEND_SUCCESS_MESSAGE'));

      // Nettoyer le formulaire et redémarrer le countdown
      this.clearForm();
      this.canResend = false; // Réinitialiser canResend avant de redémarrer le countdown
      this.startCountdown();
    } catch (error: any) {
      this.isResending = false;
      this.handleApiError(error);
    }
  }

  onBack(): void {
    this.router.navigate(['/auth/login']);
  }

  /**
   * Gérer les erreurs API selon la structure NestJS
   * Avec messages contextuels et accessibilité
   */
  private handleApiError(error: ApiError): void {
    let errorMessage = this.translate.instant('VERIFICATION.ERROR_MESSAGE');
    let errorTitle = this.translate.instant('VERIFICATION.ERROR_TITLE');
    let errorIcon = 'pi pi-times-circle';
    let shouldClearForm = true;

    // Erreur "bad credential" ou code invalide
    if (error.error === 'bad credential' || error.message === 'Invalid OTP' || error.message === 'Invalid credentials') {
      errorMessage = this.translate.instant('VERIFICATION.INVALID_OTP');
      errorIcon = 'pi pi-exclamation-triangle';
    }
    // Code expiré
    else if (error.statusCode === 410 || error.message?.includes('expired')) {
      errorMessage = this.translate.instant('VERIFICATION.EXPIRED_CODE');
      errorTitle = this.translate.instant('VERIFICATION.CODE_EXPIRED_TITLE');
      errorIcon = 'pi pi-clock';
      shouldClearForm = false;
    }
    // Erreur de réseau
    else if (error.statusCode === 0 || error.statusCode === 500) {
      errorMessage = this.translate.instant('VERIFICATION.NETWORK_ERROR');
      errorTitle = this.translate.instant('COMMON.CONNECTION_ERROR');
      errorIcon = 'pi pi-wifi';
      shouldClearForm = false;
    }
    // Trop de tentatives
    else if (error.statusCode === 429) {
      errorMessage = this.translate.instant('VERIFICATION.TOO_MANY_ATTEMPTS');
      errorIcon = 'pi pi-ban';
    }
    // Message de l'API
    else if (typeof error.message === 'string') {
      errorMessage = error.message;
    } else if (Array.isArray(error.message)) {
      errorMessage = error.message.join(', ');
    }

    // Définir l'erreur pour l'affichage
    this.hasError = true;
    this.errorMessage = errorMessage;

    // Nettoyer le formulaire si nécessaire
    if (shouldClearForm) {
      this.clearForm();
    }

    // Afficher le toast
    this.messageService.add({
      severity: 'error',
      summary: errorTitle,
      detail: errorMessage,
      life: 5000,
      icon: errorIcon,
    });

    // Annoncer pour l'accessibilité
    this.announce(`${errorTitle}: ${errorMessage}`);

    // Log pour debugging
    console.error('Verification Error:', {
      statusCode: error.statusCode,
      error: error.error,
      message: error.message,
      path: error.path,
    });
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach((key) => {
      const control = formGroup.get(key);
      control?.markAsTouched();
    });
  }

  private clearForm(): void {
    this.verificationForm.reset();
    const inputs = this.codeInputs.toArray();
    inputs.forEach((input) => {
      input.nativeElement.value = '';
    });
    if (inputs[0]) {
      inputs[0].nativeElement.focus();
    }
  }

  get isFormValid(): boolean {
    return this.verificationForm.valid;
  }
}
